import React, { useEffect, useState, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import FormInput from '../../input/FormInput';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Grid, TextField, Button } from '@material-ui/core';
import { mathOperators, mathCriteriaOptions, externalPricesOptions } from './select-options';
import useReactHookFormStyles from '../../../../../custom-components/form-components/ReactHookForm.styles';
import { generateTestId } from '../../../../../../utils';
import AddExistingPaymentLineItemRateDialog from '../../../../../main/apps/ConfiguredApps/payment-line-item-configs/dialogs/AddExistingPaymentLineItemRateDialog';
import { Add as AddIcon } from '@mui/icons-material';

function ComplexPaymentRate({ name, disabledOnEdit = false }) {
    const formulaInputRef = useRef();
    const [formulaCursorPosition, setFormulaCursorPosition] = useState(0);
    const { watch, setValue } = useFormContext();
    const [mathOperation, setMathOperation] = useState(null);
    const [mathCriteria, setMathCriteria] = useState(null);
    const [externalPrice, setExternalPrice] = useState(null);
    const [addRateDialogOpen, setAddRateDialogOpen] = useState(false);
    const classes = useReactHookFormStyles();
    const formula = watch(`${name}.rateFormula`);

    const updateFormulaCursorPosition = () => {
        setFormulaCursorPosition(formulaInputRef.current.selectionStart);
    };

    const setComplexRate = (expression) => {
        if (formula == null) {
            setValue(`${name}.rateFormula`, expression);
        } else {
            const newFormula = `${formula.substring(0, formulaCursorPosition)}${expression}${formula.substring(formulaCursorPosition)}`;
            setValue(`${name}.rateFormula`, newFormula);
        }
    };

    const handleAddRate = (rate) => {
        const rateIdentifier = `@EXECUTE_RATE(${rate.unique_id})`;
        setComplexRate(rateIdentifier);
    };

    useEffect(() => {
        if (mathOperation?.id != null) {
            setComplexRate(mathOperation.id);
            setMathOperation(null);
        }
    }, [mathOperation]);

    useEffect(() => {
        if (mathCriteria?.id != null) {
            setComplexRate(mathCriteria.id);
            setMathCriteria(null);
        }
    }, [mathCriteria]);

    useEffect(() => {
        if (externalPrice?.id != null) {
            setComplexRate(`@EXTERNAL_PRICE(${externalPrice.id})`);
            setExternalPrice(null);
        }
    }, [externalPrice]);

    return (
        <>
            {addRateDialogOpen && (
                <AddExistingPaymentLineItemRateDialog
                    dialogOpen={addRateDialogOpen}
                    handleClose={() => setAddRateDialogOpen(false)}
                    handleAddExistingRate={handleAddRate}
                    fixedRatesOnly
                    // wrap
                />
            )}
            <Grid container spacing={2}>
                <Grid item xs={3}>
                    <Autocomplete
                        size="small"
                        value={mathOperation}
                        options={mathOperators}
                        onChange={(_e, value) => setMathOperation(value)}
                        renderInput={(params) => <TextField {...params} variant={'outlined'} label="Add Operation" className={classes.textField} data-testid={generateTestId(name, 'add-operation-textfield')} />}
                        getOptionLabel={(option) => option.name}
                        data-testid={generateTestId(name, 'add-operation-autocomplete')}
                        disabled={disabledOnEdit}
                        // wrap
                    />
                </Grid>
                <Grid item xs={3}>
                    <Autocomplete
                        size="small"
                        value={mathCriteria}
                        options={mathCriteriaOptions}
                        onChange={(_e, value) => setMathCriteria(value)}
                        renderInput={(params) => <TextField {...params} variant={'outlined'} label="Add Criteria" className={classes.textField} data-testid={generateTestId(name, 'add-criteria-textfield')} />}
                        getOptionLabel={(option) => option.name}
                        data-testid={generateTestId(name, 'add-criteria-autocomplete')}
                        disabled={disabledOnEdit}
                        // wrap
                    />
                </Grid>
                <Grid item xs={3}>
                    <Autocomplete
                        size="small"
                        value={externalPrice}
                        options={externalPricesOptions}
                        onChange={(_e, value) => setExternalPrice(value)}
                        renderInput={(params) => <TextField {...params} variant={'outlined'} label="Add External Price" className={classes.textField} data-testid={generateTestId(name, 'add-external-price-textfield')} />}
                        getOptionLabel={(option) => option.name}
                        data-testid={generateTestId(name, 'add-external-price-autocomplete')}
                        disabled={disabledOnEdit}
                        // wrap
                    />
                </Grid>
                <Grid item xs={3}>
                    <Button
                        variant="outlined"
                        aria-label="add fixed rate"
                        startIcon={<AddIcon />}
                        onClick={() => setAddRateDialogOpen(true)}
                        disabled={disabledOnEdit}
                        fullWidth
                        // wrap
                    >
                        Add Fixed Rate
                    </Button>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormInput name={`${name}.rateFormula`} props={{ label: 'Rate', required: true, variant: 'outlined', multiline: true, rows: 4, maxRows: 4, onSelect: updateFormulaCursorPosition, inputRef: formulaInputRef, disabled: disabledOnEdit }} />
                </Grid>
            </Grid>
        </>
    );
}

export default ComplexPaymentRate;
